import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import { Camera, Upload, ArrowLeft, Loader2, CheckCircle2, Mail, Copy } from "lucide-react";

export default function UploadInvoice() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useT();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [inboundEmail, setInboundEmail] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/inbound/address").then(({ data }) => setInboundEmail(data.email)).catch(() => {});
  }, []);

  const onFile = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/jpg"].includes(f.type)) {
      setError(t("up.invalid_format"));
      return;
    }
    setError("");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/invoices", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      navigate(`/cliente/fatura/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setUploading(false);
    }
  };

  return (
    <div className="paper-grain min-h-screen">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="upload-page">
        <Link to="/cliente" className="inline-flex items-center gap-2 text-sm text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]" data-testid="back-to-dashboard">
          <ArrowLeft size={16} /> {t("common.back")}
        </Link>
        <span className="label-eyebrow block mt-6">{t("up.eyebrow")}</span>
        <h1 className="font-display text-3xl sm:text-4xl mt-2">{t("up.title")}</h1>
        <p className="text-[color:var(--ink-muted)] mt-3">{t("up.subtitle")}</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-6">
          <div
            className={`card-soft p-0 overflow-hidden ${preview ? "" : "border-dashed"}`}
            data-testid="upload-dropzone"
          >
            {preview ? (
              <img src={preview} alt="Pré-visualização" className="w-full max-h-[460px] object-contain bg-[color:var(--paper)]" data-testid="preview-image" />
            ) : (
              <div className="p-10 text-center text-[color:var(--ink-muted)]">
                <Camera className="mx-auto" size={32} />
                <p className="mt-3">{t("up.no_image")}</p>
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              ref={cameraRef}
              onChange={(e) => onFile(e.target.files?.[0])}
              className="hidden"
              data-testid="camera-input"
            />
            <button type="button" onClick={() => cameraRef.current?.click()} className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="take-photo-btn">
              <Camera size={18} /> {t("up.take_photo")}
            </button>

            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              ref={fileRef}
              onChange={(e) => onFile(e.target.files?.[0])}
              className="hidden"
              data-testid="file-input"
            />
            <button type="button" onClick={() => fileRef.current?.click()} className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="choose-file-btn">
              <Upload size={18} /> {t("up.choose_file")}
            </button>
          </div>

          {error && (
            <div className="text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-lg p-3" data-testid="upload-error">{error}</div>
          )}

          <button
            type="submit"
            disabled={!file || uploading}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 text-base"
            data-testid="submit-invoice-btn"
          >
            {uploading ? (
              <><Loader2 size={18} className="animate-spin" /> {t("up.uploading")}</>
            ) : (
              <><CheckCircle2 size={18} /> {t("up.submit")}</>
            )}
          </button>
          {uploading && (
            <p className="text-xs text-[color:var(--ink-muted)] text-center">{t("up.uploading_hint")}</p>
          )}
        </form>

        <div className="mt-10 card-soft" data-testid="email-forward-card">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-[color:var(--primary)]" />
            <span className="label-eyebrow">{t("up.email_eyebrow")}</span>
          </div>
          <h2 className="font-display text-xl mt-2">{t("up.email_title")}</h2>
          <p className="text-sm text-[color:var(--ink-muted)] mt-1">{t("up.email_desc")}</p>
          {inboundEmail ? (
            <div className="mt-4 space-y-3">
              <div className="bg-[color:var(--paper)] border border-[color:var(--border)] rounded-lg p-3 flex items-center gap-2">
                <span className="flex-1 font-mono-data text-sm break-all" data-testid="inbound-email-address">{inboundEmail}</span>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(inboundEmail); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                  className="btn-secondary text-xs inline-flex items-center gap-1"
                  data-testid="copy-inbound-email-btn"
                >
                  <Copy size={13} /> {copied ? t("up.copied") : t("up.copy")}
                </button>
              </div>
              <p className="text-xs text-[color:var(--ink-muted)]">
                {t("up.email_from_note")} <span className="font-mono-data text-[color:var(--ink)]">{user?.email}</span>
              </p>
            </div>
          ) : (
            <p className="mt-4 text-sm text-[color:var(--ink-muted)]" data-testid="inbound-unavailable">{t("up.email_unavailable")}</p>
          )}
        </div>
      </main>
    </div>
  );
}
